'use client';

import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox } from '@shared/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover';
import { cn } from '@shared/utils/utils';
import { Loader2, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Normaliza un texto removiendo acentos y diacríticos
 * Ejemplo: "México" -> "Mexico", "café" -> "cafe"
 */
const normalizeText = (text: string): string => {
  return text
    .normalize('NFD') // Descomponer caracteres Unicode
    .replace(/[\u0300-\u036f]/g, '') // Remover diacríticos
    .toLowerCase();
};

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Texto secundario opcional (ej: código del producto) */
  description?: string;
}

/** Respuesta de la función loadOptions para paginación */
export interface LoadOptionsResponse {
  options: MultiSelectOption[];
  hasMore: boolean;
}

interface MultiSelectProps {
  /** Opciones estáticas (modo simple sin paginación) */
  options?: MultiSelectOption[];
  /** Función para cargar opciones con paginación y búsqueda (modo dinámico) */
  loadOptions?: (params: { search: string; page: number; limit: number }) => Promise<LoadOptionsResponse>;
  /** Valores seleccionados (array de strings) */
  value: string[];
  /** Callback cuando cambian los valores seleccionados */
  onValueChange: (value: string[]) => void;
  /** Texto placeholder cuando no hay selección */
  placeholder?: string;
  /** Texto cuando no hay opciones disponibles */
  emptyText?: string;
  /** Texto cuando no hay resultados de búsqueda */
  noResultsText?: string;
  /** Clases CSS adicionales para el botón trigger */
  triggerClassName?: string;
  /** Si el componente está deshabilitado */
  disabled?: boolean;
  /** Placeholder para el campo de búsqueda */
  searchPlaceholder?: string;
  /** Data test id */
  dataTestId?: string;
  /** Número de items por página (default: 20) */
  pageSize?: number;
  /** Debounce en ms para la búsqueda (default: 300) */
  searchDebounce?: number;
}

/**
 * Componente MultiSelect
 *
 * Selector múltiple con checkboxes, búsqueda y scroll infinito.
 * Soporta dos modos:
 * - Estático: opciones pasadas via prop `options`
 * - Dinámico: carga con `loadOptions` con paginación y búsqueda remota
 */
export const MultiSelect = ({
  options: staticOptions,
  loadOptions,
  value,
  onValueChange,
  placeholder = 'Seleccionar...',
  emptyText = 'No hay opciones disponibles',
  noResultsText = 'Sin resultados',
  triggerClassName,
  disabled = false,
  searchPlaceholder = 'Buscar...',
  dataTestId = 'multi-select',
  pageSize = 20,
  searchDebounce = 300,
}: MultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dynamicOptions, setDynamicOptions] = useState<MultiSelectOption[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadingMoreRef = useRef(false); // Ref para evitar race conditions

  // Determinar si estamos en modo dinámico
  const isDynamic = !!loadOptions;

  // Opciones a mostrar
  const options = useMemo(() => {
    if (isDynamic) {
      return dynamicOptions;
    }
    // Modo estático: filtrar por búsqueda local (ignorando acentos)
    if (!staticOptions) return [];
    if (!search) return staticOptions;
    const normalizedSearch = normalizeText(search);
    return staticOptions.filter((opt) => {
      const normalizedLabel = normalizeText(opt.label);
      const normalizedDescription = opt.description ? normalizeText(opt.description) : '';
      return normalizedLabel.includes(normalizedSearch) || normalizedDescription.includes(normalizedSearch);
    });
  }, [isDynamic, dynamicOptions, staticOptions, search]);

  // Obtener opciones seleccionadas (buscar en todas las fuentes)
  const selectedOptions = useMemo(() => {
    const allOptions = isDynamic ? dynamicOptions : staticOptions || [];
    return allOptions.filter((option) => value?.includes(option.value));
  }, [isDynamic, dynamicOptions, staticOptions, value]);

  // Debounce para la búsqueda
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, searchDebounce);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search, searchDebounce]);

  // Ref para trackear la última búsqueda cargada y evitar duplicados
  const lastLoadedSearchRef = useRef<string | null>(null);
  const isLoadingRef = useRef(false);

  // Efecto único para cargar opciones (inicial y cambios de búsqueda)
  useEffect(() => {
    if (!isDynamic || !loadOptions || !open) return;

    // Evitar cargar si ya estamos cargando o si ya cargamos esta búsqueda
    if (isLoadingRef.current) return;
    if (lastLoadedSearchRef.current === debouncedSearch && isInitialized) return;

    const loadData = async () => {
      isLoadingRef.current = true;
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
      setIsLoading(true);
      setPage(1);
      setHasMore(true);

      try {
        const result = await loadOptions({
          search: debouncedSearch,
          page: 1,
          limit: pageSize,
        });
        setDynamicOptions(result.options);
        setHasMore(result.hasMore);
        setIsInitialized(true);
        lastLoadedSearchRef.current = debouncedSearch;
      } catch (error) {
        console.error('[MultiSelect.loadEffect] Error loading options:', error);
        setDynamicOptions([]);
        setHasMore(false);
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    };

    loadData();
  }, [isDynamic, loadOptions, debouncedSearch, pageSize, open, isInitialized]);

  // Cargar más opciones (scroll infinito)
  const loadMore = useCallback(async () => {
    // Usar ref para evitar múltiples llamadas simultáneas
    if (!isDynamic || !loadOptions || !hasMore || isLoadingMoreRef.current || isLoading) {
      return;
    }

    // Marcar que estamos cargando (ref + state)
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    const nextPage = page + 1;

    try {
      const result = await loadOptions({
        search: debouncedSearch,
        page: nextPage,
        limit: pageSize,
      });

      // Solo actualizar si hay nuevas opciones
      if (result.options.length > 0) {
        setDynamicOptions((prev) => [...prev, ...result.options]);
        setPage(nextPage);
      }
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Error loading more options:', error);
      setHasMore(false); // Detener más intentos si hay error
    } finally {
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [isDynamic, loadOptions, hasMore, isLoading, page, debouncedSearch, pageSize]);

  // Detectar scroll para cargar más
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      // No hacer nada si ya estamos cargando
      if (isLoadingMoreRef.current || isLoading || !hasMore) {
        return;
      }

      const target = e.target as HTMLDivElement;
      const { scrollHeight, scrollTop, clientHeight } = target;

      // Calcular distancia al final
      const scrollBottom = scrollHeight - scrollTop - clientHeight;

      // Cargar más cuando estamos cerca del final (100px para mejor UX)
      if (scrollBottom < 100) {
        loadMore();
      }
    },
    [hasMore, isLoading, loadMore],
  );

  // Toggle selección de opción
  const handleSelect = useCallback(
    (optionValue: string) => {
      const currentValue = value || [];
      const isSelected = currentValue.includes(optionValue);
      let newValue;
      if (isSelected) {
        newValue = currentValue.filter((v) => v !== optionValue);
      } else {
        newValue = [...currentValue, optionValue];
      }
      onValueChange(newValue);
    },
    [value, onValueChange],
  );

  // Texto para mostrar en el trigger
  const displayText = useMemo(() => {
    if (selectedOptions.length === 0) {
      // Verificar si hay valores seleccionados pero no tenemos las opciones cargadas
      if (value && value.length > 0) {
        return `${value.length} seleccionados`;
      }
      return placeholder;
    }
    if (selectedOptions.length === 1) {
      return selectedOptions[0].label;
    }
    return `${selectedOptions.length} seleccionados`;
  }, [selectedOptions, placeholder, value]);

  const hasSelection = (value && value.length > 0) || selectedOptions.length > 0;

  // Reset al cerrar
  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearch('');
    }
  }, []);

  return (
    <Popover open={open && !disabled} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-card-border bg-card-bg px-3 py-2 text-sm ring-offset-background',
            'hover:bg-accent hover:text-accent-foreground',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            disabled && 'cursor-not-allowed opacity-50',
            !hasSelection && 'text-muted-foreground',
            hasSelection && 'text-foreground',
            triggerClassName,
          )}
          data-testid={dataTestId}
        >
          <span className="truncate">{displayText}</span>
          <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
            <FontAwesomeIcon
              icon={faChevronDown}
              className={cn(
                'h-2.5 w-2.5 opacity-50 text-muted-foreground transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-body-bg border border-card-border"
        align="start"
        style={{ width: 'var(--radix-popover-trigger-width)', minWidth: '220px' }}
      >
        {/* Campo de búsqueda */}
        <div className="flex items-center border-b border-card-border px-3">
          <Search className="h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-10 w-full bg-transparent py-3 px-2 text-sm outline-none placeholder:text-muted-foreground"
          />
          {isLoading && <Loader2 className="h-4 w-4 animate-spin opacity-50" />}
        </div>

        {/* Lista de opciones con scroll */}
        <div ref={listRef} className="max-h-[280px] overflow-y-auto" onScroll={handleScroll}>
          {isLoading && options.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              Cargando...
            </div>
          ) : options.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">{search ? noResultsText : emptyText}</div>
          ) : (
            <>
              {options.map((option) => {
                const currentValue = value || [];
                const isSelected = currentValue.includes(option.value);
                return (
                  <div
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors',
                      'hover:bg-hover-press',
                      isSelected && 'bg-accent/30',
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleSelect(option.value)}
                      className="h-4 w-4 flex-shrink-0 dark:data-[state=checked]:bg-title dark:text-body-bg light:data-[state=checked]:!bg-badge-status-4 light:!text-text light:data-[state=checked]:border-0"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="truncate text-sm block">{option.label}</span>
                      {option.description && (
                        <span className="truncate text-xs text-muted-foreground block">{option.description}</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Indicador de carga para scroll infinito */}
              {isLoadingMore && (
                <div className="py-3 text-center text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
